import { computed, ref } from 'vue';
import { IRouter } from '@/models/router/router.interface';
import { DashboardAccessItem } from '@/models/dashboard-data.interface';
import { defaultSizeConfiguration, graduateEmploymentSizeConfiguration } from '@/static/dashboard-size-configurations';
import { TableauViz, TableauEventType } from '@tableau/embedding-api';
import store from '@/store';

export default class EmbeddedDashboardCode {
	public tableauJWT: string;
	private dashboardWidthGraduateEmployment = ref(graduateEmploymentSizeConfiguration.width);
	private dashboardHeightGraduateEmployment = ref(graduateEmploymentSizeConfiguration.height);
	private tableauViz: TableauViz | null = null;
	private boundUpdateDashboardSize = this.updateDashboardSize.bind(this);

	constructor() {
		// Ensure that the JWT is not reactive, to prevent the dashboard re-rendering on every fresh token fetch.
		this.tableauJWT = this.tableauJWTFromStore.value;
	}

	public dashboardUrl = computed((): string => {
		const relevantDashboard = this.availableDashboards.value.find((dashboard => dashboard.id === this.dashboardId.value));
		const url = relevantDashboard ? relevantDashboard.url : '';
		return `${url}?:showAppBanner=false&:display_count=n&:showVizHome=n&:origin=viz_share_link`;
	});

	public dashboardWidth = computed((): number => {
		if (this.dashboardId.value !== graduateEmploymentSizeConfiguration.id) {
			return defaultSizeConfiguration.width;
		}

		return this.dashboardWidthGraduateEmployment.value;
	});

	public dashboardHeight = computed((): number => {
		if (this.dashboardId.value !== graduateEmploymentSizeConfiguration.id) {
			return defaultSizeConfiguration.height;
		}

		return this.dashboardHeightGraduateEmployment.value;
	});

	public dashboardId = computed((): string => {
		return this.router.value.props.id;
	});

	private tableauJWTFromStore = computed((): string => {
		return store.getters.tableauJWT();
	});

	private availableDashboards = computed((): DashboardAccessItem[] => {
		return store.getters.availableDashboards();
	});

	private router = computed((): IRouter => {
		return store.getters.router();
	});

	public mounted(): void {
		this.tableauViz = document.getElementById('tableauViz') as TableauViz | null;
		if (!this.tableauViz || this.dashboardId.value !== graduateEmploymentSizeConfiguration.id) {
			return;
		}

		const foundTableauViz = this.tableauViz;

		foundTableauViz.addEventListener(TableauEventType.FirstInteractive, () => {
			// Apply the correct size for the initial tab.
			this.boundUpdateDashboardSize();
	
			foundTableauViz.addEventListener(TableauEventType.TabSwitched, this.boundUpdateDashboardSize);
		}, { once: true });
	}

	public unmounted(): void {
		if (!this.tableauViz) {
			return;
		}

		this.tableauViz.removeEventListener(TableauEventType.TabSwitched, this.boundUpdateDashboardSize);
	}

	private updateDashboardSize(): void {
		if (!this.tableauViz) {
			return;
		}

		const workbook = this.tableauViz.workbook;

		const { maxSize } = workbook.activeSheet.size;
		if (!maxSize) {
			return;
		}

		const extraSpaceToAvoidScrollbar = 50;
		// Extra space so the entire dashboard fits; the sizes don't always reflect the actual dashboard size.
		this.dashboardWidthGraduateEmployment.value = maxSize.width + extraSpaceToAvoidScrollbar;
		this.dashboardHeightGraduateEmployment.value = maxSize.height + extraSpaceToAvoidScrollbar;
	}
}
